import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';

import {
	AnalyticsActionSubject,
	triggerAnalyticsForAddToTeam,
	triggerAnalyticsForAddToTeamFailed,
} from '../components/analytics';
import type { Product } from '../types/product';
import { type InvitedUser } from '../types/user';
import { postJson } from '../utils/fetch';

import { useInviteToProduct } from './use-invite-to-product';

type Member = {
	accountId: string;
};

type AddToTeamResponse = {
	members: Member[];
	errors: {
		accountId: string;
		code: string;
		message: string;
	}[];
};

const addToTeam = async ({
	members,
	orgId,
	teamId,
	createAnalyticsEvent,
	source,
	fireEvent,
}: {
	members: Member[];
	orgId: string;
	teamId: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	source?: string;
	fireEvent: ReturnType<typeof useAnalyticsEventsNext>['fireEvent'];
}) => {
	try {
		if (members.length === 0) {
			return;
		}

		const url = `/gateway/api/public/teams/v1/org/${orgId}/teams/${teamId}/members/add`;

		const data = await postJson<AddToTeamResponse>(url, {
			members,
		});

		if (data?.members.length > 0) {
			if (fg('ptc-enable-teams-common-analytics-refactor')) {
				fireEvent('track.invitedTeamMembers.added', {
					numberOfMembers: data.members.length,
					members: data.members,
					integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
				});
			} else {
				triggerAnalyticsForAddToTeam({
					source,
					createAnalyticsEvent,
					numberOfMembers: data?.members.length,
					extraAnalyticsAttrs: {
						members: data?.members,
					},
				});
			}
		}

		if (data.errors.length > 0) {
			if (fg('ptc-enable-teams-common-analytics-refactor')) {
				fireEvent('track.addToTeamService.failed', {
					errorsCount: data.errors.length,
					errors: data.errors,
					integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
					message: null,
				});
			} else {
				triggerAnalyticsForAddToTeamFailed({
					createAnalyticsEvent,
					extraAnalyticsAttrs: {
						errorsCount: data.errors.length,
						errors: data.errors,
					},
				});
			}
		}
	} catch (error) {
		const typeGuardedError =
			error instanceof Error ? error : new Error('Failed to add users to team');
		if (fg('ptc-enable-teams-common-analytics-refactor')) {
			fireEvent('track.addToTeamService.failed', {
				message: typeGuardedError.message,
				integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
				errorsCount: null,
				errors: null,
			});
		} else {
			triggerAnalyticsForAddToTeamFailed({
				createAnalyticsEvent,
				extraAnalyticsAttrs: {
					message: typeGuardedError.message,
				},
			});
		}
	}
};

export const useInviteAndAddToTeam = ({
	products,
	source,
	originProduct,
}: {
	products: Product[];
	source?: string;
	originProduct: string;
}) => {
	const inviteToProduct = useInviteToProduct({ products, source, originProduct });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireEvent } = useAnalyticsEventsNext();

	return async ({
		newUsersWhoNeedInvite,
		cloudId,
		teamId,
		orgId,
	}: {
		newUsersWhoNeedInvite: InvitedUser[];
		cloudId: string;
		teamId: string;
		orgId?: string;
	}) => {
		if (newUsersWhoNeedInvite.length === 0) {
			return;
		}

		const newUsersEmails = newUsersWhoNeedInvite.map((selectedMember) => selectedMember.id);

		const inviteToProductResult = await inviteToProduct(newUsersEmails, cloudId);

		if (!inviteToProductResult || !orgId) {
			return;
		}

		return new Promise<void>((resolve) => {
			// There is a permission sync which occurs for new invitees. The timeout is to increase chances of success for the add to team
			setTimeout(async () => {
				const members: Member[] = [];
				inviteToProductResult.forEach((invitee) => {
					if (invitee.id) {
						members.push({
							accountId: invitee.id,
						});
					}
				});
				await addToTeam({
					members,
					orgId,
					teamId,
					createAnalyticsEvent,
					source,
					fireEvent,
				});
				resolve();
			}, 6000);
		});
	};
};
